import { apiClient } from './apiClient';
import { ExtractionSchema, ExtractionSchemaCreate, ExtractionSchemaUpdate, SchemaFilters, FieldDefinitions } from '../types';

const BASE_URL = '/admin/schemas';
const TENANT_ACTIVE_URL = '/tenant/me/schemas/active';
const ADMIN_ACTIVE_URL = '/admin/schemas/active';

export const schemaService = {
  async list(filters?: SchemaFilters): Promise<ExtractionSchema[]> {
    const params = new URLSearchParams();
    if (filters?.domain) params.append('domain', filters.domain);
    if (filters?.include_deprecated) params.append('include_deprecated', 'true');

    const url = params.toString() ? `${BASE_URL}?${params}` : BASE_URL;
    const response = await apiClient.get<ExtractionSchema[]>(url);
    return response.data;
  },

  async get(id: string): Promise<ExtractionSchema> {
    const response = await apiClient.get<ExtractionSchema>(`${BASE_URL}/${id}`);
    return response.data;
  },

  async getActive(domain: string): Promise<ExtractionSchema> {
    const response = await apiClient.get<ExtractionSchema>(`${TENANT_ACTIVE_URL}/${domain}`);
    return response.data;
  },

  async getActiveForClient(domain: string, clientId: string): Promise<ExtractionSchema> {
    const response = await apiClient.get<ExtractionSchema>(
      `${ADMIN_ACTIVE_URL}/${domain}?client_id=${encodeURIComponent(clientId)}`
    );
    return response.data;
  },

  async create(data: ExtractionSchemaCreate): Promise<ExtractionSchema> {
    const response = await apiClient.post<ExtractionSchema>(BASE_URL, data);
    return response.data;
  },

  async update(id: string, data: ExtractionSchemaUpdate): Promise<ExtractionSchema> {
    const response = await apiClient.put<ExtractionSchema>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  async activate(id: string): Promise<ExtractionSchema> {
    const response = await apiClient.post<ExtractionSchema>(`${BASE_URL}/${id}/activate`);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  async getSchemaFieldDefinitions(id: string): Promise<FieldDefinitions> {
    const response = await apiClient.get<ExtractionSchema>(`${BASE_URL}/${id}`);
    return response.data.field_definitions!;
  },

  async updateSchemaFieldDefinitions(id: string, fieldDefinitions: FieldDefinitions): Promise<void> {
    await apiClient.patch(`${BASE_URL}/${id}`, { field_definitions: fieldDefinitions });
  },

  async previewInstructions(id: string): Promise<string> {
    const response = await apiClient.get<{ instructions: string }>(`${BASE_URL}/${id}/preview-instructions`);
    return response.data.instructions;
  },

  async bootstrapFromGT(schemaId: string): Promise<ExtractionSchema> {
    const response = await apiClient.post<ExtractionSchema>(`${BASE_URL}/${schemaId}/bootstrap-from-gt`);
    return response.data;
  }
};
